import { ItemListRelativeTime } from "./ItemListRelativeTime";
import * as helpers from "../../../shared/lib/helpers";

export interface CompareItemPriceSnapshotItem {
  item_id?: string | number;
  mall_name?: string | null;
  item_title?: string | null;
  item_url?: string | null;
  price_exposed?: number | null;
  price_exposed_includes_shipping_fee?: boolean | null;
  price?: number | null;
  discount?: number | null;
  discount_by_market?: number | null;
  shipping_fee?: number | null;
}

export interface CompareItemPriceSnapshotData {
  collected_at?: string;
  compare_range?: string;
  rank_price_basis?: string;
  gap_price_basis?: string;
  items?: unknown;
}

export const parseCompareItemPriceSnapshotItems = (
  value: unknown,
): CompareItemPriceSnapshotItem[] => {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is CompareItemPriceSnapshotItem =>
        typeof item === "object" && item !== null,
    );
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const formatPrice = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return "-";
  }
  return `${helpers.formatNumber(value)}원`;
};

interface CompareItemPriceSnapshotProps {
  snapshot?: CompareItemPriceSnapshotData | null;
}

const CompareItemPriceSnapshot = ({ snapshot }: CompareItemPriceSnapshotProps) => {
  const items = parseCompareItemPriceSnapshotItems(snapshot?.items);

  return (
    <>
      {snapshot?.collected_at && (
        <div className="form-text mb-2">
          수집일시: <ItemListRelativeTime timestamp={snapshot.collected_at} />
        </div>
      )}
      {(snapshot?.rank_price_basis || snapshot?.gap_price_basis) && (
        <div className="form-text mb-2">
          순위 기준: {snapshot.rank_price_basis === "price" ? "판매가" : "노출가"}
          {" / "}
          차액 기준: {snapshot.gap_price_basis === "price" ? "판매가" : "노출가"}
        </div>
      )}
      {items.length === 0 ? (
        <div className="text-center text-muted py-4">
          {!snapshot?.collected_at
            ? "성공한 실행의 가격 스냅샷이 없습니다."
            : "수집된 경쟁상품 상세 가격이 없습니다."}
        </div>
      ) : (
        <div className="table-responsive">
          <table
            className="table table-sm align-middle text-nowrap text-center"
            style={{ fontSize: "0.875rem" }}
          >
            <thead>
              <tr>
                <th>상품명</th>
                <th>업체명</th>
                <th>판매가</th>
                <th>판매자 할인</th>
                <th>마켓 할인</th>
                <th>배송비</th>
                <th>노출가</th>
              </tr>
            </thead>
            <tbody>
              {items.map((snapshotItem, index) => (
                <tr key={`${snapshotItem.item_id || "snapshot"}-${index}`}>
                  <td
                    className="text-start"
                    title={snapshotItem.item_title || ""}
                    style={{ maxWidth: "20rem" }}
                  >
                    {snapshotItem.item_title ? (
                      snapshotItem.item_url ? (
                        <a
                          href={snapshotItem.item_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-inline-block text-truncate align-middle"
                          style={{ maxWidth: "20rem" }}
                        >
                          {snapshotItem.item_title}
                        </a>
                      ) : (
                        snapshotItem.item_title
                      )
                    ) : "-"}
                  </td>
                  <td className="text-start">{snapshotItem.mall_name || ""}</td>
                  <td>{formatPrice(snapshotItem.price)}</td>
                  <td>{formatPrice(snapshotItem.discount)}</td>
                  <td>{formatPrice(snapshotItem.discount_by_market)}</td>
                  <td>{formatPrice(snapshotItem.shipping_fee)}</td>
                  <td>
                    {formatPrice(snapshotItem.price_exposed)}
                    {typeof snapshotItem.price_exposed_includes_shipping_fee === "boolean" && (
                      <small className="d-block text-muted">
                        배송비 {snapshotItem.price_exposed_includes_shipping_fee ? "포함" : "미포함"}
                      </small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default CompareItemPriceSnapshot;
